import React, { useEffect, useState } from 'react';
import { services } from '../mock';
import { ChevronLeft, ChevronRight, Award, ArrowRight } from 'lucide-react';
import { listProperties } from '../lib/api';
import PropertyCard from './PropertyCard';
import { Skeleton } from './ui/skeleton';
import { Link } from 'react-router-dom';
import ServiceBookingModal from './ServiceBookingModal';

const SERVICE_KEYS = {
  'Legal': 'legal',
  'Taxation': 'taxation',
  'Home Inspection': 'home_inspection',
  'Home Loan': 'home_loan',
};

export const ExpertiseSection = () => {
  const [bookingService, setBookingService] = useState(null);
  return (
  <section id="services" className="py-14 md:py-20 bg-white">
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight">EXPLORE OUR <span className="text-[#9a1b2f]">EXPERTISE</span></h2>
        <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {services.map((s) => {
          const key = SERVICE_KEYS[s.title];
          return (
            <button
              type="button"
              key={s.title}
              onClick={() => setBookingService(key)}
              className="group relative bg-white rounded-2xl border border-gray-100 p-5 md:p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-[#9a1b2f]/30"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#9a1b2f] rounded-t-2xl scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3">{s.title}</h3>
              <div className="w-full aspect-square max-w-[140px] mx-auto flex items-center justify-center">
                <img src={s.img} alt={s.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#9a1b2f] group-hover:underline">
                {s.subtitle} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
    <ServiceBookingModal open={!!bookingService} onOpenChange={(o) => !o && setBookingService(null)} service={bookingService} />
  </section>
  );
};

export const DistressDealsSection = () => {
  const [tab, setTab] = useState('resale');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(0);
  const perView = 4;

  const labelMap = { resale: 'RESALE', rent: 'RENTING', resale_commercial: 'COMMERCIAL' };
  const tabs = ['resale', 'rent', 'resale_commercial'];

  useEffect(() => {
    setLoading(true);
    setStart(0);
    listProperties({ type: tab, limit: 12 })
      .then((data) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const canPrev = start > 0;
  const canNext = start + perView < items.length;

  return (
    <section className="py-14 md:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-[#9a1b2f]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#9a1b2f]">Best Value</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight">DISTRESS DEALS</h2>
          <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
        </div>

        <div className="flex justify-center flex-wrap gap-2 mb-6">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-full text-xs md:text-sm font-bold uppercase tracking-wider transition-all ${
                tab === t
                  ? 'bg-[#9a1b2f] text-white shadow-md'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#9a1b2f] hover:text-[#9a1b2f]'
              }`}
            >
              {labelMap[t]}
            </button>
          ))}
        </div>

        <div className="text-center mb-6">
          <Link to={`/listings?type=${tab}`} className="text-sm text-gray-600 hover:text-[#9a1b2f]">
            Want To Explore All <span className="text-[#9a1b2f] font-semibold underline">{labelMap[tab]} Distress Deals</span>
          </Link>
        </div>

        <div className="relative">
          <div className="mobile-section-scroll md:overflow-hidden">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Skeleton className="aspect-[4/3] w-full" />
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="mobile-section-track grid grid-flow-col auto-cols-[85%] sm:auto-cols-[45%] lg:auto-cols-[24%] gap-4 transition-transform duration-500"
                style={{ transform: `translateX(calc(-${start} * (100% / ${perView} + 1rem / ${perView})))` }}
              >
                {items.map((p) => <PropertyCard key={p.id} p={p} />)}
              </div>
            )}
          </div>
          {items.length > perView && (
            <>
              <button
                disabled={!canPrev}
                onClick={() => setStart((s) => Math.max(0, s - 1))}
                className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center hover:bg-[#9a1b2f] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                disabled={!canNext}
                onClick={() => setStart((s) => Math.min(items.length - perView, s + 1))}
                className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center hover:bg-[#9a1b2f] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
